import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import API, { apiError } from "@/lib/api";
import { Hexagon, Loader2, Check, X } from "lucide-react";

const field = "mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white outline-none focus:border-emerald-500/50";

const DIAL_CODES = [
  { code: "+91", label: "India (+91)" },
  { code: "+1", label: "USA/Canada (+1)" },
  { code: "+44", label: "UK (+44)" },
  { code: "+971", label: "UAE (+971)" },
  { code: "+61", label: "Australia (+61)" },
  { code: "+65", label: "Singapore (+65)" },
  { code: "+92", label: "Pakistan (+92)" },
  { code: "+880", label: "Bangladesh (+880)" },
  { code: "+977", label: "Nepal (+977)" },
  { code: "+94", label: "Sri Lanka (+94)" },
  { code: "+63", label: "Philippines (+63)" },
  { code: "+62", label: "Indonesia (+62)" },
  { code: "+60", label: "Malaysia (+60)" },
  { code: "+966", label: "Saudi Arabia (+966)" },
  { code: "+234", label: "Nigeria (+234)" },
  { code: "+27", label: "South Africa (+27)" },
  { code: "+49", label: "Germany (+49)" },
  { code: "+33", label: "France (+33)" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", mobile: "", country_code: "+91", country: "", password: "", confirm_password: "",
    referral_code: "", terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [sponsor, setSponsor] = useState({ status: "idle", name: "" }); // idle | found | notfound

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref }));
  }, [params]);

  // Debounced sponsor name lookup (public endpoint) as the referral code is typed.
  useEffect(() => {
    const code = (form.referral_code || "").trim();
    if (!code) { setSponsor({ status: "idle", name: "" }); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const r = await API.get(`/public/referral/${encodeURIComponent(code)}`);
        if (!active) return;
        setSponsor(r.data.found ? { status: "found", name: r.data.name } : { status: "notfound", name: "" });
      } catch {
        if (active) setSponsor({ status: "idle", name: "" });
      }
    }, 400);
    return () => { active = false; clearTimeout(t); };
  }, [form.referral_code]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setMobile = (e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.terms) return toast.error("Please accept the terms");
    if (form.mobile.length !== 10) return toast.error("Mobile number must be exactly 10 digits");
    if (form.password.length < 5) return toast.error("Password must be at least 5 characters");
    if (form.password !== form.confirm_password) return toast.error("Passwords do not match");
    if (form.referral_code && sponsor.status === "notfound") return toast.error("Invalid referral code");
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created — welcome!");
      nav("/app");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-bg min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg fade-up">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500">
            <Hexagon className="h-6 w-6 text-slate-900" />
          </span>
          <div className="font-display font-extrabold text-xl text-white">Rightwayp2p</div>
        </Link>
        <div className="glass rounded-3xl p-8">
          <h1 className="font-display text-2xl font-bold text-white">Create your account</h1>
          <p className="text-sm text-slate-400 mt-1 mb-6">Join the network with a sponsor's referral code</p>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wide text-slate-400 font-mono">Full Name</label>
              <input data-testid="register-name" required className={field} value={form.name} onChange={set("name")} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400 font-mono">Email</label>
              <input data-testid="register-email" type="email" required className={field} value={form.email} onChange={set("email")} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400 font-mono">Mobile</label>
              <div className="mt-1.5 flex gap-2">
                <select data-testid="register-country-code" value={form.country_code} onChange={set("country_code")}
                  className="w-28 shrink-0 rounded-xl bg-white/5 border border-white/10 px-2 py-3 text-white outline-none focus:border-emerald-500/50">
                  {DIAL_CODES.map((d) => <option key={d.label} value={d.code} className="bg-slate-900">{d.code}</option>)}
                </select>
                <input data-testid="register-mobile" required inputMode="numeric" maxLength={10}
                  placeholder="10-digit number" className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white outline-none focus:border-emerald-500/50"
                  value={form.mobile} onChange={setMobile} />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400 font-mono">Country</label>
              <input data-testid="register-country" required className={field} value={form.country} onChange={set("country")} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400 font-mono">Referral Code</label>
              <input data-testid="register-referral" className={field} placeholder="optional" value={form.referral_code} onChange={set("referral_code")} />
              {sponsor.status === "found" && (
                <p data-testid="referral-sponsor-name" className="mt-1.5 text-xs text-emerald-400 flex items-center gap-1">
                  <Check className="h-3.5 w-3.5" /> Sponsor: {sponsor.name}
                </p>
              )}
              {sponsor.status === "notfound" && (
                <p data-testid="referral-invalid" className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                  <X className="h-3.5 w-3.5" /> No user found for this code
                </p>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400 font-mono">Password</label>
              <input data-testid="register-password" type="password" required minLength={5} className={field} value={form.password} onChange={set("password")} />
              <p className="mt-1 text-[0.65rem] text-slate-500">Minimum 5 characters</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400 font-mono">Confirm Password</label>
              <input data-testid="register-confirm" type="password" required minLength={5} className={field} value={form.confirm_password} onChange={set("confirm_password")} />
            </div>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-400 mt-1">
              <input data-testid="register-terms" type="checkbox" checked={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.checked })} className="h-4 w-4 accent-emerald-500" />
              I accept the terms & conditions
            </label>
            <button data-testid="register-submit" disabled={loading}
              className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-slate-900 font-semibold py-3 rounded-xl transition-colors">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create Account
            </button>
          </form>
          <p className="text-sm text-slate-400 mt-6 text-center">
            Already registered? <Link to="/login" className="text-emerald-400 hover:text-emerald-300 font-medium">Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
